import React from "react";
import { X, Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Link } from "react-router-dom";

import macaronStrawberry from "../assets/macaron-strawberry.png";
import macaronVanilla    from "../assets/macaron-vanilla.png";
import macaronEspresso   from "../assets/macaron-chocolate.png";
import macaronPeppermint from "../assets/macaron-peppermint.png";
import macaronLemon      from "../assets/macaron-lemon.png";
import macaronPeach      from "../assets/macaron-peach.png";
import macaronToastedCoconut from "../assets/macaron-toasted-coconut.png";

const FLAVOR_IMAGES = {
  vanilla:            macaronVanilla,
  strawberry:         macaronStrawberry,
  espresso:           macaronEspresso,
  peppermint:         macaronPeppermint,
  lemon:              macaronLemon,
  peach:              macaronPeach,
  "toasted-coconut":  macaronToastedCoconut,
};

function CartItemImage({ flavorId }) {
  const src = FLAVOR_IMAGES[flavorId];
  if (!src) return null;
  return (
    <img
      src={src}
      alt={flavorId}
      className="w-12 h-12 object-contain flex-shrink-0 drop-shadow-sm"
    />
  );
}

export default function CartDrawer() {
  const { isOpen, items, totalItems, totalPrice, dispatch } = useCart();

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-50 backdrop-blur-sm"
          onClick={() => dispatch({ type: "CLOSE_CART" })}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-cream-50 z-50 shadow-2xl flex flex-col transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-cream-200 bg-white">
          <div className="flex items-center gap-2">
            <ShoppingBag size={20} className="text-sage-700" />
            <span className="font-display text-lg text-sage-800">Your Cart</span>
            {totalItems > 0 && (
              <span className="bg-sage-100 text-sage-700 text-xs font-bold px-2 py-0.5 rounded-full">
                {totalItems}
              </span>
            )}
          </div>
          <button
            onClick={() => dispatch({ type: "CLOSE_CART" })}
            className="p-1.5 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <ShoppingBag size={48} className="mx-auto mb-4 opacity-30" />
              <p className="font-body text-sm">Your cart is empty</p>
              <p className="text-xs mt-1">Browse menu to get started!</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-cream-200">
                <CartItemImage flavorId={item.flavorId} />
                <div className="flex-1 min-w-0">
                  <p className="font-body font-bold text-sm text-sage-800 truncate">{item.name}</p>
                  <p className="text-xs text-gray-500">${item.price.toFixed(2)} each</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => dispatch({ type: "UPDATE_QTY", id: item.id, qty: item.qty - 1 })}
                    className="w-6 h-6 rounded-full border border-cream-300 flex items-center justify-center text-sage-700 hover:bg-sage-50 transition-colors"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="w-5 text-center font-bold text-sm">{item.qty}</span>
                  <button
                    onClick={() => dispatch({ type: "UPDATE_QTY", id: item.id, qty: item.qty + 1 })}
                    className="w-6 h-6 rounded-full border border-cream-300 flex items-center justify-center text-sage-700 hover:bg-sage-50 transition-colors"
                  >
                    <Plus size={12} />
                  </button>
                  <button
                    onClick={() => dispatch({ type: "REMOVE_ITEM", id: item.id })}
                    className="ml-1 text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-5 py-5 border-t border-cream-200 bg-white space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-body text-gray-600 text-sm">Subtotal</span>
              <span className="font-display text-lg text-sage-800">${totalPrice.toFixed(2)}</span>
            </div>
            <Link
              to="/order"
              onClick={() => dispatch({ type: "CLOSE_CART" })}
              className="btn-primary block text-center w-full"
            >
              Proceed to Checkout
            </Link>
            <button
              onClick={() => dispatch({ type: "CLEAR" })}
              className="w-full text-center text-xs text-gray-400 hover:text-red-400 transition-colors py-1"
            >
              Clear cart
            </button>
          </div>
        )}
      </div>
    </>
  );
}
